import { estimateMutationScopeId } from '@/api/mutations/estimates/hydrateEstimateCaches';
import useAddEstimateGrant from '@/api/mutations/estimates/useAddEstimateGrant';
import useRemoveEstimateGrant from '@/api/mutations/estimates/useRemoveEstimateGrant';
import useGetMyAccountGrants from '@/api/queries/me/useGetMyAccountGrants';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import { Button, EmptyState, SkeletonLoader } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC } from 'react';

type GrantsTabProps = {
  projectId: string;
  estimateId: string;
};

const GrantsTab: FC<GrantsTabProps> = (props) => {
  const { projectId, estimateId } = props;
  const { estimate } = useGetProjectEstimate(projectId, estimateId);
  const { grants: catalogue, isLoading } = useGetMyAccountGrants();

  const addEstimateGrant = useAddEstimateGrant({
    projectId,
    estimateId,
    options: { scope: { id: estimateMutationScopeId(estimateId) } },
  });
  const removeEstimateGrant = useRemoveEstimateGrant({
    projectId,
    estimateId,
    options: { scope: { id: estimateMutationScopeId(estimateId) } },
  });

  if (!estimate) return null;

  const attached = estimate.grants ?? [];
  const attachedSourceIds = new Set(
    attached.flatMap((g) => (g.source ? [g.source.id] : []))
  );
  const unattached = catalogue.filter((g) => !attachedSourceIds.has(g.id));

  if (!isLoading && catalogue.length === 0 && attached.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          iconName="link-broken-01.3"
          text="No grants in your account catalogue yet."
        />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3 typography:prose">
      {attached.map((grant) => (
        <AttachedGrantCard
          key={grant.id}
          grant={grant}
          onRemove={() => removeEstimateGrant.mutate({ id: grant.id })}
          isRemoving={removeEstimateGrant.isPending}
        />
      ))}
      {isLoading ? (
        <CatalogueGrantSkeleton />
      ) : (
        unattached.map((grant) => (
          <CatalogueGrantCard
            key={grant.id}
            grant={grant}
            onApply={() =>
              addEstimateGrant.mutate({
                estimateId,
                accountGrantId: grant.id,
              })
            }
            isApplying={addEstimateGrant.isPending}
          />
        ))
      )}
    </div>
  );
};

const CatalogueGrantSkeleton: FC = () => {
  return (
    <div className="flex gap-3 rounded-lg border p-4">
      <div className="min-w-0 flex-1 space-y-2">
        <SkeletonLoader className="h-4 w-40" />
        <SkeletonLoader className="h-3 w-56" />
        <SkeletonLoader className="h-3 w-24" />
      </div>
      <SkeletonLoader className="h-8 w-20 self-center rounded-md" />
    </div>
  );
};

type AttachedGrant = NonNullable<
  ReturnType<typeof useGetProjectEstimate>['estimate']
>['grants'][number];

type AttachedGrantCardProps = {
  grant: AttachedGrant;
  onRemove: () => void;
  isRemoving: boolean;
};

const AttachedGrantCard: FC<AttachedGrantCardProps> = (props) => {
  const { grant, onRemove, isRemoving } = props;
  return (
    <div
      className={clstx(
        'flex gap-3 rounded-lg border p-4',
        'border-green-200 bg-green-50'
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{grant.name}</p>
        {grant.description && (
          <p className="supporting-body mt-1">{grant.description}</p>
        )}
        <p className="font-medium mt-2 text-green-700">
          −<CurrencyValue {...grant.amount} />
        </p>
      </div>
      <Button
        variant="white"
        size="sm"
        className="self-center"
        aria-label={`Remove ${grant.name}`}
        onClick={onRemove}
        disabled={isRemoving}
      >
        <UntitledIcon name="x.3" className="size-4" />
      </Button>
    </div>
  );
};

type CatalogueGrant = ReturnType<
  typeof useGetMyAccountGrants
>['grants'][number];

type CatalogueGrantCardProps = {
  grant: CatalogueGrant;
  onApply: () => void;
  isApplying: boolean;
};

const CatalogueGrantCard: FC<CatalogueGrantCardProps> = (props) => {
  const { grant, onApply, isApplying } = props;
  return (
    <div className="flex gap-3 rounded-lg border p-4">
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{grant.name}</p>
        {grant.description && (
          <p className="supporting-body mt-1">{grant.description}</p>
        )}
        <p className="supporting-body mt-2">
          <CurrencyValue {...grant.availableAmount} />
        </p>
      </div>
      <Button
        variant="white"
        size="sm"
        className="self-center"
        aria-label={`Add ${grant.name}`}
        onClick={onApply}
        isProcessing={isApplying}
        disabled={isApplying}
      >
        + Apply
      </Button>
    </div>
  );
};

export default GrantsTab;
